/**
 * O segmented control do Lumma Store — o trilho cinza com o item ativo em
 * branco.
 *
 * Havia DEZ implementações deste controle no sistema, e elas não eram cópias:
 * eram versões que divergiram. O protótipo define fundo `#F3F4F6`, item ativo
 * BRANCO com sombra, `padding 9px 16px` e 13,5px. No código encontrei
 *
 *   · dashboard   branco + sombra, 13px,   `role="tab"` / `aria-selected`
 *   · estoque     branco + sombra, 12,5px, `aria-pressed`
 *   · cliente     AZUL,            13,5px, `aria-pressed`
 *   · fornecedor  AZUL,            13,5px, `aria-pressed`
 *
 * Duas com a cor errada, três com a tipografia errada, e o mesmo gesto sendo
 * anunciado como "aba" numa tela e como "botão pressionado" na outra.
 *
 * ## Por que a semântica é uma prop, e não uma escolha de quem escreve
 *
 * Os dois papéis são legítimos, mas dizem coisas diferentes:
 *
 *   `aba`    troca o CONTEÚDO visível abaixo. Vira `role="tab"` dentro de um
 *            `role="tablist"`, com navegação por seta e roving tabindex — é o
 *            que o leitor de tela anuncia como "aba 2 de 3".
 *   `filtro` recorta a MESMA lista. Vira `aria-pressed`, porque não há painel
 *            para o `aria-controls` apontar, e "aba" prometeria uma troca de
 *            contexto que não acontece.
 *
 * A regra prática: se o que está abaixo continua sendo a mesma coisa com menos
 * linhas, é filtro. Se vira outro assunto, é aba.
 */
import { useRef, type KeyboardEvent as EventoDeTecla, type ReactNode } from 'react'

export type OpcaoSegmentada<T extends string> = {
  chave: T
  rotulo: ReactNode
  /** Contagem à direita do rótulo — "Camisetas 24". */
  contagem?: number
  /**
   * Nome para o leitor de tela quando `rotulo` é só um ícone — o alternador
   * lista/cartões do catálogo é o caso. Sem ele, o item vira "botão" e pronto.
   */
  rotuloAcessivel?: string
}

export function Segmentado<T extends string>({
  opcoes,
  valor,
  aoTrocar,
  papel = 'filtro',
  rotuloDoGrupo,
  compacto = false,
}: {
  opcoes: OpcaoSegmentada<T>[]
  valor: T
  aoTrocar: (chave: T) => void
  papel?: 'aba' | 'filtro'
  /** Obrigatório no papel `aba` — é o nome do `tablist` no leitor de tela. */
  rotuloDoGrupo?: string
  /**
   * Trilho mais apertado, para quando o controle divide a barra com busca e
   * seletor. Encolhe o RECUO, nunca a fonte: 12,5px na toolbar e 13,5px no
   * formulário eram duas escalas para o mesmo controle.
   */
  compacto?: boolean
}) {
  const trilho = useRef<HTMLDivElement>(null)

  /**
   * Seta anda entre as abas, como manda o padrão de `tablist`. Sem isto, quem
   * navega por teclado gasta uma parada de Tab por aba antes de chegar ao
   * conteúdo.
   *
   * Só no papel `aba`: num grupo de filtros os itens são botões independentes,
   * e o Tab entre eles é o comportamento esperado.
   */
  function aoTeclar(evento: EventoDeTecla) {
    if (papel !== 'aba') return
    const passo = evento.key === 'ArrowRight' ? 1 : evento.key === 'ArrowLeft' ? -1 : 0
    if (passo === 0) return
    evento.preventDefault()

    const indice = opcoes.findIndex((o) => o.chave === valor)
    const proxima = opcoes[(indice + passo + opcoes.length) % opcoes.length]
    if (!proxima) return

    aoTrocar(proxima.chave)
    // O foco acompanha a seleção: numa tablist automática, quem chegou pela
    // seta espera continuar dali com Tab.
    requestAnimationFrame(() => {
      trilho.current?.querySelector<HTMLElement>(`[data-chave="${proxima.chave}"]`)?.focus()
    })
  }

  return (
    <div
      ref={trilho}
      role={papel === 'aba' ? 'tablist' : 'group'}
      aria-label={rotuloDoGrupo}
      onKeyDown={aoTeclar}
      // `flex-wrap` e `min-w-0` são a regra 4 do "layout que já custou bug" da
      // spec 01: um trilho de sete categorias numa toolbar estreita precisa
      // quebrar, e não empurrar a busca para fora da tela.
      className="inline-flex min-w-0 flex-wrap gap-[3px] rounded-sistema bg-divisoria p-[3px]"
    >
      {opcoes.map((opcao) => {
        const ativa = opcao.chave === valor

        return (
          <button
            key={opcao.chave}
            type="button"
            data-chave={opcao.chave}
            role={papel === 'aba' ? 'tab' : undefined}
            aria-selected={papel === 'aba' ? ativa : undefined}
            aria-pressed={papel === 'filtro' ? ativa : undefined}
            aria-label={opcao.rotuloAcessivel}
            // Roving tabindex: numa tablist, só a aba ativa é alcançável por
            // Tab. Num grupo de filtros, todas são.
            tabIndex={papel === 'aba' && !ativa ? -1 : undefined}
            onClick={() => aoTrocar(opcao.chave)}
            className={`flex items-center gap-1.5 rounded-[6px] text-[13.5px] font-semibold whitespace-nowrap transition-colors duration-150 ${
              compacto ? 'px-3 py-[7px]' : 'px-4 py-[9px]'
            } ${
              ativa
                ? // Branco com sombra — o desenho do protótipo. O ativo em AZUL
                  // que duas telas usavam confundia "esta é a seção aberta" com
                  // "este item está selecionado", que é o azul da linha de
                  // tabela e o das pills.
                  'bg-superficie text-tinta shadow-[0_1px_2px_rgba(0,0,0,0.08)]'
                : 'text-texto-suave hover:text-texto'
            }`}
          >
            {opcao.rotulo}
            {opcao.contagem !== undefined && (
              <span
                className={`tabular text-[11.5px] ${ativa ? 'text-texto-suave' : 'text-texto-fraco'}`}
              >
                {opcao.contagem}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
